import React, { useEffect, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";

const LIMIT = 10;

export const Maintenance = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [stats, setStats] = useState({ todayAmount: 0, weekAmount: 0, monthAmount: 0, avgAmount: 0, totalRecords: 0, spendByType: {} });
  const [filterVehicle, setFilterVehicle] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vehicles, setVehicles] = useState([]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const openEdit = (item) => setEditItem(item);
  const closeEdit = () => setEditItem(null);

  const buildParams = (pageVal, vehicleId, from, to) => {
    const params = { page: pageVal, limit: LIMIT };
    if (vehicleId) params.vehicleId = vehicleId;
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;
    return params;
  };

  const getData = async (pageVal = page, vehicleId = filterVehicle, from = dateFrom, to = dateTo) => {
    try {
      setLoader(true);
      const response = await api.getMaintenance(buildParams(pageVal, vehicleId, from, to));
      if (response?.success) {
        setData(response?.records || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  const getStats = async (vehicleId = filterVehicle, from = dateFrom, to = dateTo) => {
    try {
      const params = { page: 1, limit: 1000 };
      if (vehicleId) params.vehicleId = vehicleId;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
      const response = await api.getMaintenance(params);
      if (response?.success) {
        const records = response?.records || [];
        const now = moment();
        const n = (v) => parseFloat(v) || 0;
        const todayAmount = records.filter(r => moment(r.date).isSame(now, "day")).reduce((sum, r) => sum + n(r.amount), 0);
        const weekAmount = records.filter(r => moment(r.date).isAfter(moment().subtract(7, "days"))).reduce((sum, r) => sum + n(r.amount), 0);
        const monthAmount = records.filter(r => moment(r.date).isSame(now, "month")).reduce((sum, r) => sum + n(r.amount), 0);
        const avgAmount = records.length > 0 ? Math.round(records.reduce((sum, r) => sum + n(r.amount), 0) / records.length) : 0;
        const spendByType = {};
        records.filter(r => moment(r.date).isSame(now, "month")).forEach(r => {
          const t = r?.type || "others";
          spendByType[t] = (spendByType[t] || 0) + n(r.amount);
        });
        setStats({ todayAmount, weekAmount, monthAmount, avgAmount, totalRecords: records.length, spendByType });
      }
    } catch (e) {}
  };

  useEffect(() => {
    getData(1);
    getStats();
    api.getVehicle().then(v => { if (v?.success) setVehicles(v?.vehicles || []); }).catch(() => {});
  }, []);

  const applyFilters = (vehicleId, from, to) => { setPage(1); getData(1, vehicleId, from, to); getStats(vehicleId, from, to); };
  const handleVehicleFilter = (vehicleId) => { setFilterVehicle(vehicleId); applyFilters(vehicleId, dateFrom, dateTo); };
  const handleDateFrom = (val) => { setDateFrom(val); applyFilters(filterVehicle, val, dateTo); };
  const handleDateTo = (val) => { setDateTo(val); applyFilters(filterVehicle, dateFrom, val); };
  const clearFilters = () => { setFilterVehicle(""); setDateFrom(""); setDateTo(""); applyFilters("", "", ""); };

  const handlePageChange = (e, value) => { setPage(value); getData(value); };

  const delteMaintenance = async (id) => {
    if (!window.confirm("Are you sure you want to delete this maintenance record?")) return;
    try {
      const response = await api.delMaintenance(id);
      if (response?.success) {
        notify.success("Maintenance record deleted successfully!");
        getData(page);
        getStats();
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  const handlePrint = async () => {
    try {
      const params = { page: 1, limit: 1000 };
      if (filterVehicle) params.vehicleId = filterVehicle;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await api.getMaintenance(params);
      const records = response?.records || [];
      const selectedVehicle = vehicles.find(v => v._id === filterVehicle);
      const filterLabel = [
        selectedVehicle ? `Vehicle: ${selectedVehicle.numberPlate?.toUpperCase()}` : "",
        dateFrom ? `From: ${moment(dateFrom).format("MMM Do YYYY")}` : "",
        dateTo ? `To: ${moment(dateTo).format("MMM Do YYYY")}` : "",
      ].filter(Boolean).join("  |  ");
      const totalAmount = records.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      const win = window.open("", "_blank");
      win.document.write(`<!DOCTYPE html><html><head><title>Maintenance Report — Shabbir Transport</title>
        <style>
          body{font-family:Arial,sans-serif;padding:28px;color:#1a1a1a;font-size:13px}
          h2{margin:0 0 4px;font-size:20px}
          .meta{color:#666;font-size:12px;margin-bottom:6px}
          .filter-label{color:#4338ca;font-size:12px;font-weight:600;margin-bottom:16px}
          table{width:100%;border-collapse:collapse;margin-top:8px}
          th{background:#f1f5f9;font-weight:700;padding:8px 10px;border:1px solid #ddd;text-align:left;font-size:12px}
          td{padding:7px 10px;border:1px solid #e5e7eb;font-size:12px}
          tr:nth-child(even) td{background:#f8fafc}
          .amount{font-weight:700;color:#065f46}
          tfoot td{font-weight:700;background:#f1f5f9;font-size:13px}
          @media print{body{padding:16px}}
        </style>
      </head><body>
        <h2>Shabbir Transport — Maintenance Report</h2>
        <div class="meta">Generated: ${moment().format("MMM Do YYYY, h:mm a")} &nbsp;·&nbsp; Total Records: ${records.length}</div>
        ${filterLabel ? `<div class="filter-label">Filters: ${filterLabel}</div>` : ""}
        <table>
          <thead><tr>
            <th>#</th><th>Date</th><th>Number Plate</th><th>Type</th>
            <th>Amount</th><th>Description</th><th>Pay Through</th><th>Remarks</th>
          </tr></thead>
          <tbody>
            ${records.map((r, i) => `<tr>
              <td>${i + 1}</td>
              <td>${moment(r.date).format("MMM Do YYYY")}</td>
              <td><b>${r.vehicleId?.numberPlate?.toUpperCase() || "—"}</b></td>
              <td style="text-transform:capitalize">${r.type || "—"}</td>
              <td class="amount">Rs. ${Number(r.amount || 0).toLocaleString()}</td>
              <td style="text-transform:capitalize">${r.description || "—"}</td>
              <td>${r.paymentMethod?.name || "—"}</td>
              <td style="text-transform:capitalize">${r.remarks || "—"}</td>
            </tr>`).join("")}
          </tbody>
          <tfoot><tr>
            <td colspan="4"></td>
            <td class="amount">Total: Rs. ${totalAmount.toLocaleString()}</td>
            <td colspan="3"></td>
          </tr></tfoot>
        </table>
      </body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    } catch (e) {
      notify.error("Could not generate report");
    }
  };

  const hasFilters = filterVehicle || dateFrom || dateTo;
  const selectedVehicle = vehicles.find(v => v._id === filterVehicle);

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Maintenance ({total})</h3>
              <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: 6, background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                <i className="fa-solid fa-file-arrow-down" /> Download / Print
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-car" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" }} />
                <select value={filterVehicle} onChange={e => handleVehicleFilter(e.target.value)}
                  style={{ paddingLeft: 28, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", outline: "none", minWidth: 150, cursor: "pointer" }}>
                  <option value="">All Vehicles</option>
                  {vehicles.map(v => <option key={v._id} value={v._id}>{v.numberPlate?.toUpperCase()}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>From</span>
                <input type="date" value={dateFrom} onChange={e => handleDateFrom(e.target.value)}
                  style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>To</span>
                <input type="date" value={dateTo} onChange={e => handleDateTo(e.target.value)}
                  style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }} />
              </div>
              {hasFilters && (
                <button onClick={clearFilters}
                  style={{ background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 7, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />Clear
                </button>
              )}
            </div>
            {hasFilters && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {selectedVehicle && <span style={{ fontSize: 11, background: "#ede9fe", color: "#6d28d9", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}><i className="fa-solid fa-car" style={{ marginRight: 4 }} />{selectedVehicle.numberPlate?.toUpperCase()}</span>}
                {dateFrom && <span style={{ fontSize: 11, background: "#dbeafe", color: "#1d4ed8", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>From: {moment(dateFrom).format("MMM Do YYYY")}</span>}
                {dateTo && <span style={{ fontSize: 11, background: "#dbeafe", color: "#1d4ed8", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>To: {moment(dateTo).format("MMM Do YYYY")}</span>}
              </div>
            )}
            <MaintenanceStats stats={stats} />
            {loader ? (
              <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
            ) : (
              <Table data={data} delteMaintenance={delteMaintenance} openEdit={openEdit} />
            )}
            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#10b981", color: "white", "&:hover": { backgroundColor: "#059669" } },
                      "& .MuiPaginationItem-root": { "&:hover": { backgroundColor: "#d1fae5", color: "#059669" } },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
        <div className="AddEmpBtn" onClick={handleShow}><i className="fa-solid fa-plus"></i></div>
      </div>
      <CreateModal show={show} handleClose={handleClose} getMaintenance={() => { getData(page); getStats(); }} />
      {editItem && <EditModal item={editItem} handleClose={closeEdit} getMaintenance={() => { getData(page); getStats(); }} />}
    </>
  );
};

const NumberPlate = ({ number }) => (
  <div style={{ display: "inline-block", background: "#fff", border: "2.5px solid #222", borderRadius: 6, overflow: "hidden", minWidth: 120, boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
    <div style={{ background: "repeating-linear-gradient(90deg, #c0392b 0px, #c0392b 10px, #e74c3c 10px, #e74c3c 14px, #c0392b 14px, #c0392b 24px)", height: 8, width: "100%" }} />
    <div style={{ padding: "4px 10px 2px", textAlign: "center", fontWeight: 900, fontSize: 15, letterSpacing: 3, color: "#111", fontFamily: "'Courier New', monospace", textTransform: "uppercase" }}>
      {number || "—"}
    </div>
    <div style={{ textAlign: "center", fontSize: 8, fontWeight: 700, letterSpacing: 4, color: "#444", paddingBottom: 4, textTransform: "uppercase" }}>
      SINDH
    </div>
  </div>
);

const maintenanceTypeColors = {
  "oil change": { bg: "#fffbeb", color: "#b45309", iconBg: "#fef3c7" },
  "tire replacement": { bg: "#eff6ff", color: "#1d4ed8", iconBg: "#dbeafe" },
  "brake service": { bg: "#fff1f2", color: "#be123c", iconBg: "#ffe4e6" },
  "engine repair": { bg: "#fff7ed", color: "#c2410c", iconBg: "#ffedd5" },
  "battery replacement": { bg: "#f0fdf4", color: "#15803d", iconBg: "#dcfce7" },
  "general service": { bg: "#f0fdfa", color: "#0f766e", iconBg: "#ccfbf1" },
  "others": { bg: "#f8fafc", color: "#475569", iconBg: "#e2e8f0" },
};

const MaintenanceStats = ({ stats }) => {
  const cards = [
    { label: "Today's Spend", value: `Rs. ${stats.todayAmount.toLocaleString()}`, icon: "fa-calendar-day", bg: "#eff6ff", color: "#1d4ed8", iconBg: "#dbeafe" },
    { label: "Last 7 Days", value: `Rs. ${stats.weekAmount.toLocaleString()}`, icon: "fa-calendar-week", bg: "#f5f3ff", color: "#7c3aed", iconBg: "#ede9fe" },
    { label: "This Month", value: `Rs. ${stats.monthAmount.toLocaleString()}`, icon: "fa-calendar", bg: "#fff7ed", color: "#c2410c", iconBg: "#ffedd5" },
    { label: "Total Records", value: stats.totalRecords, icon: "fa-list", bg: "#f8fafc", color: "#475569", iconBg: "#e2e8f0" },
  ];
  const typeEntries = Object.entries(stats.spendByType || {});
  return (
    <div style={{ marginBottom: 24, marginTop: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
        {cards.map((card, i) => (
          <div key={i} style={{ background: card.bg, borderRadius: 10, padding: "14px 16px", border: `1px solid ${card.iconBg}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fa-solid ${card.icon}`} style={{ color: card.color, fontSize: 14 }} />
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: card.color, marginBottom: 2 }}>{card.value}</div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{card.label}</div>
          </div>
        ))}
      </div>
      {typeEntries.length > 0 && (
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: "14px 18px", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            <i className="fa-solid fa-wrench" style={{ marginRight: 6 }} />Spend This Month — by Maintenance Type
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {typeEntries.map(([type, amount]) => {
              const c = maintenanceTypeColors[type] || maintenanceTypeColors["others"];
              return (
                <div key={type} style={{ background: c.bg, border: `1px solid ${c.iconBg}`, borderRadius: 8, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: c.color }}>Rs. {Math.round(amount).toLocaleString()}</span>
                  <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500, textTransform: "capitalize" }}>{type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Table = ({ data, delteMaintenance, openEdit }) => {
  const formatDate = (date) => moment(date).format("MMM Do YYYY, h:mm a");
  return (
    <div className="TableMain mt-3">
      <div className="table-responsive">
        <table className="table table-hover drivers-table">
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Number Plate</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Description</th>
              <th style={{ whiteSpace: "nowrap" }}>Pay Through</th>
              <th>Remarks</th>
              <th style={{ whiteSpace: "nowrap" }}>Date/Time</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? data.map((item, key) => (
              <tr key={key}>
                <td><NumberPlate number={item?.vehicleId?.numberPlate} /></td>
                <td><div className="table-text-dark" style={{ textTransform: "capitalize" }}>{item?.type || "—"}</div></td>
                <td>
                  <span className="table-text-dark w-max-c db" style={{ fontWeight: 700, color: "#065f46" }}>
                    Rs. {item?.amount ? Number(item.amount).toLocaleString() : "0"}
                  </span>
                </td>
                <td><span className="table-text-dark db" style={{ textTransform: "capitalize" }}>{item?.description || "—"}</span></td>
                <td><span className="table-text-dark w-max-c db">{item?.paymentMethod?.name || "—"}</span></td>
                <td><span className="table-text-dark db" style={{ textTransform: "capitalize" }}>{item?.remarks || "—"}</span></td>
                <td><span className="table-text-dark w-max-c db" style={{ fontSize: 13 }}>{formatDate(item?.date)}</span></td>
                <td>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="edit p-2 cursor-pointer" onClick={() => openEdit(item)}>
                      <i className="fa-solid fa-pen-to-square"></i>
                    </div>
                    <div className="delete p-2 cursor-pointer" onClick={() => delteMaintenance(item?._id)}>
                      <i className="fa-solid fa-trash"></i>
                    </div>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="text-center py-4 text-muted">No maintenance records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MAINTENANCE_TYPES = ["oil change","tire replacement","brake service","engine repair","battery replacement","general service","others"];

const MaintenanceForm = ({ data, onChange, vehicles, payMethods }) => (
  <div className="row">
    <div className="col-6">
      <div className="form-group">
        <label>Amount*</label>
        <input type="text" name="amount" className="form-control" value={data?.amount || ""} onChange={onChange} />
      </div>
    </div>
    <div className="col-6">
      <div className="form-group">
        <label>Type*</label>
        <select name="type" className="form-control" value={data?.type || ""} onChange={onChange}>
          <option value="">Select</option>
          {MAINTENANCE_TYPES.map((t) => <option value={t} key={t}>{t.replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Description</label>
        <textarea name="description" className="form-control" value={data?.description || ""} onChange={onChange}></textarea>
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Remarks</label>
        <textarea name="remarks" className="form-control" value={data?.remarks || ""} onChange={onChange}></textarea>
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Vehicle*</label>
        <select name="vehicleId" className="form-control" value={data?.vehicleId || ""} onChange={onChange}>
          <option value="">Select</option>
          {vehicles.map((item, key) => <option value={item?._id} key={key}>{item?.numberPlate}</option>)}
        </select>
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Payment Through*</label>
        <select name="paymentMethod" className="form-control" value={data?.paymentMethod || ""} onChange={onChange}>
          <option value="">Select</option>
          {payMethods.map((item, key) => <option value={item?._id} key={key}>{item?.name}</option>)}
        </select>
      </div>
    </div>
  </div>
);

const CreateModal = ({ show, handleClose, getMaintenance }) => {
  const [vehicles, setVehicles] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [data, setData] = useState({});
  const [loader, setLoader] = useState(false);
  const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const payload = { vehicleId: data?.vehicleId, type: data?.type, description: data?.description || "", amount: data?.amount, paymentMethod: data?.paymentMethod, remarks: data?.remarks || "" };
      const response = await api.addMaintenance(payload);
      if (response?.success) {
        getMaintenance();
        notify.success("Maintenance record successfully added!");
        setData({});
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoader(true);
        const [v, p] = await Promise.all([api.getVehicle(), api.getPayMethod()]);
        if (v?.success) setVehicles(v?.vehicles || []);
        if (p?.success) setPayMethods(p?.methods || []);
      } catch (e) {} finally { setLoader(false); }
    })();
  }, []);

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Add Maintenance Record</div>
        <MaintenanceForm data={data} onChange={onChange} vehicles={vehicles} payMethods={payMethods} />
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Submit</button>
        </div>
      </div>
    </Modal>
  );
};

const EditModal = ({ item, handleClose, getMaintenance }) => {
  const [vehicles, setVehicles] = useState([]);
  const [payMethods, setPayMethods] = useState([]);
  const [data, setData] = useState({
    amount: item?.amount || "",
    type: item?.type || "",
    description: item?.description || "",
    remarks: item?.remarks || "",
    vehicleId: item?.vehicleId?._id || "",
    paymentMethod: item?.paymentMethod?._id || "",
  });
  const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const response = await api.updateMaintenance(item._id, data);
      if (response?.success) {
        getMaintenance();
        notify.success("Maintenance record updated!");
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    (async () => {
      const [v, p] = await Promise.all([api.getVehicle(), api.getPayMethod()]);
      if (v?.success) setVehicles(v?.vehicles || []);
      if (p?.success) setPayMethods(p?.methods || []);
    })();
  }, []);

  return (
    <Modal show={true} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Edit Maintenance Record</div>
        <MaintenanceForm data={data} onChange={onChange} vehicles={vehicles} payMethods={payMethods} />
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Update</button>
        </div>
      </div>
    </Modal>
  );
};
